import { useState, useEffect, useRef } from "react";
import { Search, User, Loader2, X, AlertCircle } from "lucide-react";

const DEBOUNCE_MS = 400;

function getCandidateName(candidate) {
  if (candidate.name) return candidate.name;
  const parts = [candidate.first_name ?? candidate.First_Name, candidate.last_name ?? candidate.Last_Name]
    .filter(Boolean);
  return parts.join(" ") || "Без имени";
}

function getCandidateTitle(candidate) {
  return candidate.current_job_title
    ?? candidate.Current_Job_Title
    ?? candidate.title
    ?? "";
}

export default function CandidateSearchBar({ selectedCandidate, onSelect, onClearSelected }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      setError(null);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/candidates/search?q=${encodeURIComponent(q)}`, {
          headers: { "X-Session-ID": localStorage.getItem("session_id") || "" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setResults(Array.isArray(data) ? data : data?.candidates ?? []);
        setOpen(true);
      } catch (err) {
        setError(err.message || "Search failed");
        setResults([]);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (candidate) => {
    onSelect(candidate);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  if (selectedCandidate) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", marginBottom: 20,
        background: "rgba(200,241,53,0.06)",
        border: "1px solid rgba(200,241,53,0.2)",
        borderRadius: 8,
      }}>
        <User size={15} color="var(--acid)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#E8EDF3", fontWeight: 500 }}>
            Reupload для: {getCandidateName(selectedCandidate)}
          </div>
          {getCandidateTitle(selectedCandidate) && (
            <div style={{ fontSize: 12, color: "var(--slate-muted)" }}>
              {getCandidateTitle(selectedCandidate)}
            </div>
          )}
        </div>
        <button onClick={onClearSelected} style={{
          width: 24, height: 24, borderRadius: 6,
          background: "rgba(139,151,168,0.1)", border: "1px solid var(--slate-border)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", flexShrink: 0, color: "var(--slate-text)",
        }}>
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: "relative", marginBottom: 20 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", background: "var(--ink-800)",
        border: "1px solid var(--slate-border)", borderRadius: 8,
      }}>
        {loading
          ? <Loader2 size={15} color="var(--acid)" style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
          : <Search size={15} color="var(--slate-muted)" style={{ flexShrink: 0 }} />
        }
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length || error) setOpen(true); }}
          placeholder="Найти кандидата для reupload резюме…"
          style={{
            flex: 1, background: "transparent", border: "none", outline: "none",
            fontSize: 13, color: "#E8EDF3", fontFamily: "DM Sans, sans-serif",
          }}
        />
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
          zIndex: 20, background: "var(--ink-800)",
          border: "1px solid var(--slate-border)", borderRadius: 8,
          maxHeight: 280, overflowY: "auto",
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        }}>
          {error && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 14px", fontSize: 12, color: "#F87171",
            }}>
              <AlertCircle size={13} />
              {error}
            </div>
          )}

          {!error && !loading && results.length === 0 && (
            <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--slate-muted)" }}>
              Ничего не найдено
            </div>
          )}

          {!error && results.map((candidate) => (
            <button
              key={candidate.id ?? candidate.candidate_id ?? getCandidateName(candidate)}
              onClick={() => handleSelect(candidate)}
              style={{
                display: "flex", flexDirection: "column", gap: 2,
                width: "100%", textAlign: "left", padding: "10px 14px",
                background: "transparent", border: "none",
                borderBottom: "1px solid var(--slate-border)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(200,241,53,0.06)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 13, color: "#E8EDF3", fontWeight: 500 }}>
                {getCandidateName(candidate)}
              </span>
              {getCandidateTitle(candidate) && (
                <span style={{ fontSize: 12, color: "var(--slate-muted)" }}>
                  {getCandidateTitle(candidate)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
